import type { SgNewsResult } from 'apiConnector/discover/sgNews';
import { sgNewsConnector } from 'apiConnector/discover/sgNews';
import { LightAlert } from 'components/shared/LightAlert';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { Observable } from 'rxjs';
import { httpClient } from 'utils/http/httpClient';
import { LoadingStatus } from 'utils/status';

import { RegionContext } from '../contexts/RegionContext';
import { SgNewsItem } from './SgNewsItem';
import { SgNewsItemSkeleton } from './SgNewsItemSkeleton';

interface Props {
  fetchData(region: string): Observable<SgNewsResult>;
}

export const sgNewsApi = sgNewsConnector(httpClient);
export const Component: FC<Props> = ({ fetchData }) => {
  const { formatMessage } = useIntl();
  const [sgNewsResult, setSgNewsResult] = useState<SgNewsResult>({ news: [], events: [] });
  const [status, setStatus] = useState<LoadingStatus>('idle');

  const { region } = useContext(RegionContext);

  useEffect(() => {
    setStatus('loading');
    const subscription = fetchData(region).subscribe({
      next(data) {
        setStatus('success');
        setSgNewsResult(data);
      },
      error() {
        setStatus('error');
      },
    });
    return function cleanup() {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
    };
  }, [fetchData, region]);

  const sgNewsItems = useMemo(() => [...sgNewsResult.news, ...sgNewsResult.events], [sgNewsResult]);

  const hasNews = sgNewsItems.length > 0;

  return (
    <div className="container px-0 mt-4">
      <div className="container px-0 py-4" data-e2e="sgnews">
        <div className="d-flex justify-content-between align-items-center">
          <h1 className="mb-0">{formatMessage({ id: 'discover.news.title', defaultMessage: 'News' })}</h1>
        </div>
      </div>
      <div className="container border-top py-3">
        <div className="row">
          {status === 'loading' && (
            <>
              <SgNewsItemSkeleton />
              <SgNewsItemSkeleton />
            </>
          )}
          {status === 'error' && (
            <LightAlert severity="warning" e2e="sgNews-error">
              {formatMessage({
                id: 'discover.fetchData.error',
                defaultMessage: 'Technical error while fetching data',
              })}
            </LightAlert>
          )}
          {status === 'success' &&
            (hasNews ? (
              <>
                {sgNewsItems.map(({ image, name, description, content, creationDate, tags }, index) => (
                  <SgNewsItem
                    key={index}
                    image={image}
                    name={name}
                    description={description}
                    content={content}
                    creationDate={creationDate}
                    tags={tags}
                  />
                ))}
              </>
            ) : (
              <div data-e2e="no-sgNews">
                {formatMessage({
                  id: 'discover.sgNews.noSgNews',
                  defaultMessage: 'No SG news and events',
                })}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export const SgNews: FC = () => {
  return <Component fetchData={sgNewsApi.fetch} />;
};
